/**
 * What the signing screen of a US series asks, next to the contract (owner decision D-12 = A).
 *
 * A Mexican resident is not asked about accreditation: they confirm, once and in writing, that
 * they are not a US person and are not buying for one (Regulation S Category 3). A US person
 * answers the Rule 506(b) questionnaire again for THIS contribution - the profile only fills it
 * in - and "none of these" is an answer. Both see the DRAFT legend line for counsel.
 *
 * A US person also says they are buying for themselves, reads the resale disclosure, and meets the
 * steps of the Rule 502(b) package (`RegDPackageSection`): the server refuses the signature until
 * the ones that apply to them are done, and this screen holds the button back the same way.
 *
 * The texts come from the server, which seals the exact words the person affirmed.
 */
import type { CSSProperties } from 'react';
import type { UsOfferingAnswers, UsOfferingInfo } from '../../types/contracts';
import { colors } from '../../styles/theme';
import { ACCREDITATION_AFFIRMATIONS } from '../client/accreditationAffirmations';
import RegDPackageSection from './RegDPackageSection';

export interface UsOfferingState {
  category: string | null;
  affirmed: readonly boolean[];
  certified: boolean;
  /** 502(d)(1): "I am buying for myself, not for other persons" (Rule 506(b) only). */
  ownAccount: boolean;
}

/** The state a screen starts from: the profile's category pre-fills, nothing is affirmed. */
export function initialUsOfferingState(info: UsOfferingInfo): UsOfferingState {
  return {
    category: info.prefill_category ?? null,
    affirmed: ACCREDITATION_AFFIRMATIONS.map(() => false),
    certified: false,
    ownAccount: false,
  };
}

/**
 * Whether the person has given what the regime asks for. `stepsOwed` is what the server says is
 * still owed before a Rule 506(b) signature (null until it has said: the signature itself is the
 * gate, so an unanswered question does not hold the button back).
 */
export function usOfferingReady(
  info: UsOfferingInfo | null,
  state: UsOfferingState,
  stepsOwed: readonly string[] | null = null,
): boolean {
  if (info === null) return true;
  if (info.regime === 'reg_s_cat3') return state.certified;
  return (
    state.category !== null &&
    state.affirmed.every(Boolean) &&
    state.ownAccount &&
    (stepsOwed === null || stepsOwed.length === 0)
  );
}

/** The answers the sign request carries. */
export function usOfferingAnswers(
  info: UsOfferingInfo | null,
  state: UsOfferingState,
): UsOfferingAnswers | undefined {
  if (info === null) return undefined;
  if (info.regime === 'reg_s_cat3') return { purchaserCertification: state.certified };
  return {
    accreditationCategory: state.category,
    affirmsAccreditation: state.affirmed.every(Boolean),
    acquiringForOwnAccount: state.ownAccount,
  };
}

interface Props {
  readonly caseId: string;
  readonly info: UsOfferingInfo;
  readonly state: UsOfferingState;
  readonly disabled: boolean;
  readonly onChange: (next: UsOfferingState) => void;
  /** The steps the server says are still owed (null until it has said). */
  readonly onOwedChange: (owed: readonly string[] | null) => void;
}

const checkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

function RegulationSCertification({
  info,
  state,
  disabled,
  onChange,
}: Readonly<Props>): JSX.Element {
  return (
    <label style={{ ...checkRow, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <input
        type="checkbox"
        checked={state.certified}
        disabled={disabled}
        onChange={(e) => onChange({ ...state, certified: e.target.checked })}
        style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
      />
      <span style={{ fontSize: 13, color: colors.inkSoft, lineHeight: 1.55 }} translate="no">
        {info.certification?.text}
      </span>
    </label>
  );
}

function Questionnaire({ info, state, disabled, onChange }: Readonly<Props>): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 13, fontWeight: 600, color: colors.ink, margin: 0 }}>
        Which of these applies to you? You confirm it again for each contribution.
      </p>
      {(info.categories ?? []).map((c) => (
        <label key={c.key} style={{ ...checkRow, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <input
            type="radio"
            name="us-offering-category"
            checked={state.category === c.key}
            disabled={disabled}
            onChange={() => onChange({ ...state, category: c.key })}
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: colors.inkSoft, lineHeight: 1.55 }}>
            {c.statement}
          </span>
        </label>
      ))}
      {ACCREDITATION_AFFIRMATIONS.map((text, index) => (
        <label
          key={text}
          style={{ ...checkRow, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <input
            type="checkbox"
            checked={state.affirmed[index]}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                ...state,
                affirmed: state.affirmed.map((v, i) => (i === index ? e.target.checked : v)),
              })
            }
            style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
          />
          <span style={{ fontSize: 12, color: colors.inkSoft, lineHeight: 1.5 }}>{text}</span>
        </label>
      ))}
    </div>
  );
}

function OwnAccount({ info, state, disabled, onChange }: Readonly<Props>): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {info.resale && (
        <p style={{ fontSize: 12, color: colors.inkSoft, margin: 0, lineHeight: 1.5 }} translate="no">
          {info.resale.text}
        </p>
      )}
      <label style={{ ...checkRow, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input
          type="checkbox"
          checked={state.ownAccount}
          disabled={disabled}
          onChange={(e) => onChange({ ...state, ownAccount: e.target.checked })}
          style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
        />
        <span style={{ fontSize: 13, color: colors.inkSoft, lineHeight: 1.55 }} translate="no">
          {info.own_account?.text}
        </span>
      </label>
    </div>
  );
}

export default function UsOfferingSection(props: Readonly<Props>): JSX.Element {
  const { info, caseId, state, disabled, onOwedChange } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {info.regime === 'reg_s_cat3' ? (
        <RegulationSCertification {...props} />
      ) : (
        <>
          <Questionnaire {...props} />
          <OwnAccount {...props} />
          <RegDPackageSection
            caseId={caseId}
            category={state.category}
            disabled={disabled}
            onOwedChange={onOwedChange}
          />
        </>
      )}
      <p style={{ fontSize: 12, color: colors.muted, margin: 0, lineHeight: 1.5 }} translate="no">
        {info.legend.text}
      </p>
      {info.abroad_legend && (
        <p style={{ fontSize: 12, color: colors.muted, margin: 0, lineHeight: 1.5 }} translate="no">
          {info.abroad_legend.text}
        </p>
      )}
    </div>
  );
}
